import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Icon from '../components/Icon';
import PageSection from '../components/page/PageSection';
import FaqList from '../components/page/FaqList';
import InlineCta from '../components/page/InlineCta';
import { tie } from '../components/page/typography';
import FaqNav from '../components/faq/FaqNav';
import FaqAsk from '../components/faq/FaqAsk';
import { serviceGroups, serviceMeta } from '../content/services';
import { servicesIndexCopy, launchPlanCopy, faqIntro } from '../content/pages';

/**
 * Усі питання й відповіді з сайту в одному місці.
 *
 * Питання лишаються й на своїх сторінках — там вони відповідають людині, яка
 * вже читає про конкретну послугу. Ця сторінка для іншого випадку: коли
 * питання є, а з якої воно послуги — ще невідомо. Тому групи тут ведуть до
 * відповідних сторінок, а не дублюють їх зміст.
 *
 * Джерело одне: масиви faq із services.js і pages.js. Нового тексту тут немає
 * і бути не може — інакше дві копії відповіді розійшлися б.
 *
 * Розмітки FAQPage тут навмисно немає. Ті самі питання вже розмічені на
 * сторінках, звідки вони взяті, і дві FAQPage з однаковими питаннями лише
 * змушують Google вибирати між ними. Вибирати він мав би сторінку, звідки
 * питання взяте: там відповідь стоїть у контексті, а тут — у переліку.
 */
const groups = [
  { id: 'zagalni', title: 'Загальні питання', to: '/poslugy', linkLabel: 'Перелік послуг', faq: servicesIndexCopy.faq },
  { id: 'vidkryttya', title: 'Відкриття клініки з нуля', to: '/plan-zapusku-kliniky', linkLabel: 'План запуску клініки', faq: launchPlanCopy.faq },
  ...serviceGroups
    .map((group, i) => ({
      id: serviceMeta[i].slug,
      title: group.title,
      to: `/poslugy/${serviceMeta[i].slug}`,
      // Підпис посилання — назва сторінки, куди воно веде: безлике
      // «Детальніше» шість разів поспіль нічого не каже ні людині з екранним
      // диктором, ні пошуковику.
      linkLabel: serviceMeta[i].h1,
      faq: group.faq
    }))
    .filter((g) => g.faq && g.faq.length)
];

const total = groups.reduce((n, g) => n + g.faq.length, 0);

// Число рахується з масивів і змінюватиметься разом із ними, тож закінчення
// підбираємо під нього: «42 запитання», але «45 запитань».
const questionsWord = (n) => {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'запитань';
  if (last >= 1 && last <= 4) return 'запитання';
  return 'запитань';
};

const withCount = (n) => `${n} ${questionsWord(n)}`;
const sections = groups.map((g) => ({ ...g, countLabel: withCount(g.faq.length) }));

// Перший абзац вступу (faqIntro[0]) повторював лід банера — «питання, які
// ставлять у першій розмові», — тож тут його немає, як і на сторінці
// контактів. Решта двох коротко каже, як читати відповіді, і стоїть перед
// ними, а не після: у другому є «розділи нижче».
const introNote = faqIntro.slice(1);

// На телефоні бічного заклику немає, тож посередині переліку стоїть
// короткий: без нього до кнопки понад п'ять екранів навіть зі згорнутими
// відповідями.
const MID = Math.ceil(sections.length / 2) - 1;

const TOC_ID = 'rozdily';

const FaqPage = () => {
  return (
    <PageShell
      title="Часті запитання: ліцензія, ЕСОЗ, ФОП"
      description="Відповіді на питання про ліцензію на медичну практику, реєстрацію ФОП чи ТОВ для клініки, підключення до ЕСОЗ, документацію закладу та торговельну марку."
      path="/pytannya"
      banner="/images/page-office-18888e14.jpg"
      crumbs={[{ name: 'Часті запитання' }]}
      heading="Часті запитання"
      lead="Те, про що нас питають найчастіше — про ліцензію, форму бізнесу, ЕСОЗ, документи й строки. Якщо вашого питання тут немає, напишіть: відповімо так само конкретно."
      cta={{ source: 'faq-page' }}
    >
      {/*
        Раніше до першої відповіді йшли три абзаци вступу, картка розділів і
        фото на пів екрана, а далі — 42 розгорнуті відповіді сіткою 2×N
        карток різної висоти: двадцять один екран без жодного орієнтира.

        Тепер від lg зміст стоїть ліворуч і їде разом зі сторінкою (CSS
        sticky, без стану в React), а відповіді згорнуті: видно самі
        питання, і потрібне знаходиться поглядом. Нижче lg зміст — перший
        блок під банером, а кожен розділ закінчується посиланням назад до
        нього.
      */}
      <div className="page-container py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)] lg:gap-12 xl:gap-16">
          <div className="mb-10 lg:mb-0">
            {/* Заклик під змістом — лише там, де обидва вміщаються у висоту
                вікна: на ноутбуці 1366×768 липкий блок, вищий за вікно,
                обрізав би кнопку, тож там лишається сам зміст. На 1024
                колонка вужча, назви розділів у два рядки й зміст вищий —
                звідси інший поріг. */}
            <div className="lg:sticky lg:top-28">
              <FaqNav id={TOC_ID} title="Розділи" total={withCount(total)} groups={sections} />
              <FaqAsk className="hidden mt-3 lg:[@media(min-height:55rem)]:block xl:[@media(min-height:49rem)]:block" />
            </div>
          </div>

          <div className="min-w-0">
            <div className="grid gap-4 md:grid-cols-2 md:gap-8 pb-7 lg:pb-8 border-b border-slate-200">
              {introNote.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="text-[0.95rem] leading-relaxed text-slate-600">
                  {tie(paragraph)}
                </p>
              ))}
            </div>

            {sections.map((group, i) => (
              <React.Fragment key={group.id}>
                <PageSection
                  id={group.id}
                  caption={group.countLabel}
                  title={group.title}
                  spacing={i === 0 ? 'mt-8 lg:mt-10' : 'mt-10 lg:mt-14'}
                >
                  <FaqList items={group.faq} openFirst={i === 0} />
                  <GroupFooter to={group.to} label={group.linkLabel} />
                </PageSection>

                {i === MID && (
                  <InlineCta
                    source="faq-mid"
                    title="Не знайшли свого питання?"
                    text="Досить імені й телефону — решту з’ясуємо в розмові."
                    buttonLabel="Поставити запитання"
                    spacing="mt-10"
                    className="lg:hidden"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

/**
 * Під відповідями розділу — сторінка, де ця тема розібрана повністю. Раніше
 * посилання стояло праворуч від заголовка й на вузькій колонці то вміщалося
 * в рядок, то переносилося під нього; під переліком воно завжди на одному
 * місці і з'являється саме тоді, коли питання розділу вже прочитано.
 *
 * «До розділів» — лише на телефоні: від lg зміст і так видно збоку.
 */
const GroupFooter = ({ to, label }) => (
  <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6">
    <Link
      to={to}
      className="group inline-block py-3 text-[0.95rem] font-semibold text-teal-700 hover:text-teal-800 transition-colors duration-200"
    >
      {/* Стрілка — у тому самому рядку тексту, через нерозривний пробіл:
          коли довга назва переноситься, стрілка йде за останнім словом, а не
          висить окремо біля правого краю. */}
      <span>
        <span className="font-normal text-slate-600">Докладніше: </span>
        {label}{'\u00a0'}
        <Icon
          name="fa-arrow-right"
          className="ml-1 text-sm transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-0.5"
        />
      </span>
    </Link>
    <a
      href={`#${TOC_ID}`}
      className="lg:hidden inline-flex items-center gap-2 py-3 text-sm font-medium text-slate-600 hover:text-teal-800 transition-colors duration-200"
    >
      <Icon name="fa-arrow-left" className="rotate-90 text-xs text-teal-700" />
      До розділів
    </a>
  </div>
);

export default FaqPage;
